import React, { useEffect, useState, useMemo } from 'react';
import BaseTile from '../../../components/Tile/BaseTile';
import ContentSection from '../../../components/layout/ContentSection';
import { useGlobalAPI } from '../../../hooks/useGlobalAPI';

const StatNumber = ({ value }) => (
  <span className="font-semibold text-gray-900">
    {typeof value === 'number' ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) : '—'}
  </span>
);

/**
 * Tile listing the number of distinct event types (activities) per object type.
 * Shows top N (default 5) object types by this distinct count, sorted desc; shows all if small.
 */
const EventTypesPerObjectTypeTile = ({ activeFile, topN = 5, onRefresh }) => {
  const api = useGlobalAPI(activeFile);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!activeFile) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/ocel/object-type-activities', {});
      setData(res?.eventTypesPerObjectType || {});
    } catch (e) {
      setError(e?.message || 'Failed to load event types per object type');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api.currentFilters, activeFile]);

  // Provide refresh function to parent (GridComponent)
  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchData);
    }
  }, [onRefresh]);

  const entries = useMemo(() => {
    if (!data) return [];
    const arr = Object.entries(data);
    arr.sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
    if (arr.length <= topN + 2) return arr; // show all if small
    return arr.slice(0, topN);
  }, [data, topN]);

  return (
    <ContentSection spacing="lg">
      <BaseTile
        loading={loading}
        error={error}
        minHeight="md"
      >
        <div className="p-4">
          {entries.length === 0 && !loading && !error ? (
            <div className="text-sm text-gray-500">No data available.</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {entries.map(([type, count]) => (
                <li key={type} className="py-2 flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate pr-4" title={type}>{type}</span>
                  <StatNumber value={count} />
                </li>) )}
            </ul>
          )}
          {data && Object.keys(data).length > entries.length && (
            <div className="mt-3 text-xs text-gray-500">Showing top {entries.length} of {Object.keys(data).length} types</div>
          )}
        </div>
      </BaseTile>
    </ContentSection>
  );
};

export default EventTypesPerObjectTypeTile;
